/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ApolloError } from "@apollo/client/errors";
import dayjs from "dayjs";
import {
  Plan,
  UpdateBatchActionInput,
  UpdatePlanActionInput,
  UpdatePlanInput,
  UpdatePlanMutation,
  useUpdatePlanMutation,
} from "~/generated/graphql";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import {
  useCleanupResourceGroups,
  useCreateGroups,
} from "~/views/plans/hooks/useCreatePlan/useCreatePlan";
import { getUpdateObject } from "~/views/plans/hooks/useUpdatePlan/utils";
import { PlanData } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { PlanExtended, usePlan } from "../../PlanDetails/PlanProvider";
import { PlanFields } from "./types";

type Props = {
  onSuccess: () => void;
};

export function useUpdatePlan({ onSuccess }: Props) {
  const { plan } = usePlan();

  const [updatePlanMutation, { loading }] = useUpdatePlanMutation({
    onCompleted: (data) => {
      return handleCompleted(data, onSuccess);
    },
    onError: (error) => handleError(error),

    update(cache, { data }, { variables }) {
      if (data?.updatePlan && variables?.input.planActions) {
        cache.modify({
          id: cache.identify({
            __typename: "Plan",
            id: variables.input.id,
          }),
          fields: {
            planActions: (existing) =>
              isNotEmpty(existing) && [existing, variables.input.planActions],
          },
        });
        cache.gc();
      }
      if (data?.updatePlan && variables?.input.notificationGroups) {
        cache.modify({
          id: cache.identify({
            __typename: "Plan",
            id: variables.input.id,
          }),
          fields: {
            notificationGroups: (existing) => {
              return (
                isNotEmpty(existing) && [
                  existing,
                  variables.input.notificationGroups,
                ]
              );
            },
          },
        });

        cache.gc();
      }
    },
  });

  const { createGroups } = useCreateGroups();

  const { cleanupResourceGroups } = useCleanupResourceGroups();

  async function updatePlan(
    data: PlanFields | UpdatePlanInput,
    planData?: PlanData
  ) {
    if (!plan) {
      return;
    }

    const groupIdsByListId = planData && (await createGroups(planData));

    const updateActionWithResourceGroups = (
      action: UpdatePlanActionInput | UpdateBatchActionInput,
      listIdToGroupsMap: Record<string, string[]>
    ) => {
      const groupIds = listIdToGroupsMap[action.id ?? ""] ?? [];

      if (isEmpty(groupIds)) {
        return { ...action };
      }

      return {
        ...action,
        resourceGroupIds: [...(action.resourceGroupIds || []), ...groupIds],
      };
    };

    const planActions =
      groupIdsByListId &&
      data?.planActions
        ?.map((action) =>
          updateActionWithResourceGroups(action, groupIdsByListId)
        )
        .filter(isNotNil);

    const planActionBatches =
      groupIdsByListId &&
      data?.planActionBatches?.map((actionBatch) => ({
        ...actionBatch,
        batchActions: actionBatch?.batchActions?.map((batchAction) =>
          updateActionWithResourceGroups(batchAction, groupIdsByListId)
        ),
      }));

    const newData = {
      ...data,
      planActions: planActions,
      planActionBatches: planActionBatches,
    };

    const input = {
      ...getUpdateObject(newData, plan),
      id: plan.id ?? "",
      ...(isSpecificDatesScheduleTZUpdate(plan, data) &&
        processDates(
          plan.scheduleTimezone,
          data.scheduleTimezone,
          data.scheduleExecutionDates ?? plan.scheduleExecutionDates ?? []
        )),
    };

    const createdGroupIds = Object.entries(groupIdsByListId ?? {}).flatMap(
      ([_, groupIds]) => groupIds
    );

    if (isEmpty(createdGroupIds)) {
      return updatePlanMutation({ variables: { input } });
    }

    try {
      const result = await updatePlanMutation({ variables: { input } });
      if (result.errors) {
        cleanupResourceGroups(createdGroupIds);
      }
      return null;
    } catch (error) {
      cleanupResourceGroups(createdGroupIds);
      showError(error.message);
    }
  }
  return [updatePlan, loading] as const;
}

export function usePatchPlan({ onSuccess }: Props) {
  const [patchPlanMutation, { loading }] = useUpdatePlanMutation({
    onCompleted: (data) => handleCompleted(data, onSuccess),
    onError: (error) => handleError(error),
  });

  function patchPlan(data: UpdatePlanInput) {
    patchPlanMutation({ variables: { input: data } });
  }

  return [patchPlan, loading] as const;
}

function handleCompleted(data: UpdatePlanMutation, onSuccess: () => void) {
  if (data.updatePlan) {
    onSuccess();
  }
}

function handleError(error: ApolloError) {
  showError(error.message);
}

const isSpecificDatesScheduleTZUpdate = (
  plan: Partial<PlanExtended>,
  data: PlanFields | UpdatePlanInput
) =>
  plan?.scheduleTimezone &&
  isNotEmpty(plan.scheduleExecutionDates ?? []) &&
  data.scheduleTimezone;

function processDates(
  oldTimezone: Plan["scheduleTimezone"],
  newTimezone: Plan["scheduleTimezone"],
  dates: string[]
) {
  if (!oldTimezone || !newTimezone) {
    return {
      scheduleExecutionDates: dates,
    };
  }

  const processedDates = dates?.map((date) =>
    dayjs
      .tz(
        dayjs(date).tz(oldTimezone).format("YYYY-MM-DD HH:mm:ss"),
        newTimezone
      )
      .utc()
      .format()
  );

  return {
    scheduleExecutionDates: processedDates,
  };
}
